import {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AltRouteRounded,
  ArrowForwardRounded,
  CheckCircleRounded,
  FlagRounded,
} from '@mui/icons-material';
import {
  completeIndustrialWorkflowCaseStage,
  listIndustrialWorkflowStageTransitions,
} from '../services/industrialWorkflowsService';
import type {
  IndustrialWorkflowCase,
  IndustrialWorkflowCaseCompleteStagePayload,
  IndustrialWorkflowStageDefinition,
} from '../types/industrialWorkflows';

type Props = {
  workflowCase: IndustrialWorkflowCase | null;
  stages: IndustrialWorkflowStageDefinition[];
  open: boolean;
  onClose: () => void;
  onCompleted: () => void;
};

const EXCEPTIONAL_VALUE = '__exceptional__';

function getErrorMessage(error: unknown) {
  if (
    typeof error === 'object'
    && error !== null
    && 'response' in error
  ) {
    const response = (
      error as {
        response?: {
          data?: {
            detail?: string;
          };
        };
      }
    ).response;

    if (response?.data?.detail) {
      return response.data.detail;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'No se pudo finalizar la etapa.';
}

export default function IndustrialWorkflowCaseCompleteStageDialog({
  workflowCase,
  stages,
  open,
  onClose,
  onCompleted,
}: Props) {
  const queryClient =
    useQueryClient();

  const { enqueueSnackbar } =
    useSnackbar();

  const [
    selectedOption,
    setSelectedOption,
  ] = useState('');

  const [
    exceptionalTargetStageId,
    setExceptionalTargetStageId,
  ] = useState('');

  const [
    exceptionalReason,
    setExceptionalReason,
  ] = useState('');

  const currentStageId =
    workflowCase
      ?.current_stage_definition_id
    ?? '';

  const transitionsQuery =
    useQuery({
      queryKey: [
        'industrial-workflows-stage-transitions',
        workflowCase
          ?.process_definition_id,
        currentStageId,
        false,
      ],
      queryFn: () =>
        listIndustrialWorkflowStageTransitions(
          (
            workflowCase
              ?.process_definition_id
          ) as string,
          currentStageId,
          false,
        ),
      enabled: Boolean(
        open
        && workflowCase
        && currentStageId,
      ),
      retry: false,
    });

  const stageNameById =
    useMemo(
      () =>
        new Map(
          stages.map(
            (stage) => [
              stage.id,
              stage.name,
            ],
          ),
        ),
      [stages],
    );

  const activeTransitions =
    useMemo(
      () =>
        (
          transitionsQuery.data ?? []
        ).filter(
          (transition) =>
            transition.is_active,
        ),
      [transitionsQuery.data],
    );

  const habitualTransition =
    useMemo(
      () =>
        activeTransitions.find(
          (transition) =>
            transition.transition_key
              .toUpperCase()
            === 'DEFAULT',
        ) ?? null,
      [activeTransitions],
    );

  const otherTransitions =
    useMemo(
      () =>
        activeTransitions.filter(
          (transition) =>
            transition.transition_key
              .toUpperCase()
            !== 'DEFAULT',
        ),
      [activeTransitions],
    );

  const exceptionalTargetStages =
    useMemo(
      () =>
        stages.filter(
          (stage) =>
            stage.is_active
            && stage.id
              !== currentStageId,
        ),
      [
        currentStageId,
        stages,
      ],
    );

  useEffect(() => {
    if (!open) {
      setSelectedOption('');
      setExceptionalTargetStageId('');
      setExceptionalReason('');
      return;
    }

    if (habitualTransition) {
      setSelectedOption(
        habitualTransition.id,
      );

      return;
    }

    if (otherTransitions.length) {
      setSelectedOption(
        otherTransitions[0].id,
      );

      return;
    }

    setSelectedOption(
      EXCEPTIONAL_VALUE,
    );
  }, [
    habitualTransition,
    open,
    otherTransitions,
  ]);

  const completeStageMutation =
    useMutation({
      mutationFn: (
        payload:
          IndustrialWorkflowCaseCompleteStagePayload,
      ) =>
        completeIndustrialWorkflowCaseStage(
          (
            workflowCase
              ?.id
          ) as string,
          payload,
        ),
      onSuccess: async (
        updatedCase,
      ) => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [
              'industrial-workflows-cases',
            ],
          }),
          queryClient.invalidateQueries({
            queryKey: [
              'industrial-workflows-tasks',
              updatedCase.id,
            ],
          }),
          queryClient.invalidateQueries({
            queryKey: [
              'industrial-workflows-case-events',
              updatedCase.id,
            ],
          }),
        ]);

        enqueueSnackbar(
          'Etapa finalizada correctamente.',
          {
            variant: 'success',
          },
        );

        onCompleted();
      },
      onError: (error) => {
        enqueueSnackbar(
          getErrorMessage(error),
          {
            variant: 'error',
          },
        );
      },
    });

  const handleSubmit = () => {
    if (
      selectedOption
      === EXCEPTIONAL_VALUE
    ) {
      if (
        !exceptionalTargetStageId
      ) {
        enqueueSnackbar(
          'Selecciona la etapa de destino.',
          {
            variant: 'warning',
          },
        );

        return;
      }

      if (
        !exceptionalReason.trim()
      ) {
        enqueueSnackbar(
          'Indica el motivo del movimiento excepcional.',
          {
            variant: 'warning',
          },
        );

        return;
      }

      completeStageMutation.mutate({
        target_stage_definition_id:
          exceptionalTargetStageId,

        reason:
          exceptionalReason.trim(),
      });

      return;
    }

    if (!selectedOption) {
      enqueueSnackbar(
        'Selecciona una salida para continuar.',
        {
          variant: 'warning',
        },
      );

      return;
    }

    completeStageMutation.mutate({
      transition_definition_id:
        selectedOption,
    });
  };

  const currentStageName =
    currentStageId
      ? stageNameById.get(
          currentStageId,
        )
      : null;

  return (
    <Dialog
      open={open}
      onClose={
        completeStageMutation.isPending
          ? undefined
          : onClose
      }
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        Finalizar etapa
      </DialogTitle>

      <DialogContent>
        <Stack
          spacing={1.6}
          sx={{
            pt: 1,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: `1px solid ${alpha(
                '#38bdf8',
                0.2,
              )}`,
              bgcolor:
                'rgba(15,23,42,0.48)',
              px: 1.4,
              py: 1.2,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <CheckCircleRounded
                sx={{
                  color: '#4ade80',
                }}
              />

              <Box>
                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 900,
                  }}
                >
                  {currentStageName
                    ?? 'Etapa actual'}
                </Typography>

                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: '0.82rem',
                    mt: 0.2,
                  }}
                >
                  El sistema comprobará que no
                  queden tareas obligatorias
                  pendientes antes de continuar.
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {transitionsQuery.isError ? (
            <Alert
              severity="warning"
              variant="outlined"
            >
              No se pudieron cargar las salidas
              predefinidas. Todavía puedes registrar
              un movimiento excepcional.
            </Alert>
          ) : null}

          <Stack spacing={0.75}>
            <Typography
              sx={{
                color: '#f8fafc',
                fontWeight: 900,
              }}
            >
              ¿Hacia dónde quieres continuar?
            </Typography>

            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: '0.82rem',
              }}
            >
              La salida habitual aparece seleccionada
              inicialmente. El expediente no se moverá
              hasta que confirmes tu elección.
            </Typography>
          </Stack>

          <RadioGroup
            value={selectedOption}
            onChange={(event) =>
              setSelectedOption(
                event.target.value,
              )
            }
          >
            <Stack spacing={0.8}>
              {habitualTransition ? (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 2.5,
                    border: `1px solid ${alpha(
                      '#a78bfa',
                      0.24,
                    )}`,
                    bgcolor:
                      'rgba(15,23,42,0.36)',
                    px: 1,
                    py: 0.55,
                  }}
                >
                  <FormControlLabel
                    value={
                      habitualTransition.id
                    }
                    control={<Radio />}
                    label={
                      <Stack
                        direction="row"
                        spacing={0.8}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <ArrowForwardRounded
                          sx={{
                            color: '#c4b5fd',
                          }}
                        />

                        <Typography
                          sx={{
                            color: '#f8fafc',
                            fontWeight: 850,
                          }}
                        >
                          {stageNameById.get(
                            habitualTransition
                              .to_stage_definition_id,
                          ) ?? 'Etapa no disponible'}
                        </Typography>

                        <Chip
                          size="small"
                          label="Habitual"
                          sx={{
                            color: '#ddd6fe',
                            bgcolor: alpha(
                              '#8b5cf6',
                              0.16,
                            ),
                          }}
                        />
                      </Stack>
                    }
                  />
                </Paper>
              ) : null}

              {otherTransitions.map(
                (transition) => (
                  <Paper
                    key={transition.id}
                    elevation={0}
                    sx={{
                      borderRadius: 2.5,
                      border: `1px solid ${alpha(
                        '#38bdf8',
                        0.18,
                      )}`,
                      bgcolor:
                        'rgba(15,23,42,0.28)',
                      px: 1,
                      py: 0.55,
                    }}
                  >
                    <FormControlLabel
                      value={transition.id}
                      control={<Radio />}
                      label={
                        <Stack
                          direction="row"
                          spacing={0.8}
                          alignItems="center"
                          flexWrap="wrap"
                          useFlexGap
                        >
                          <ArrowForwardRounded
                            sx={{
                              color: '#7dd3fc',
                            }}
                          />

                          <Typography
                            sx={{
                              color: '#f8fafc',
                              fontWeight: 850,
                            }}
                          >
                            {transition.label
                              ?? 'Salida predefinida'}
                          </Typography>

                          <Typography
                            sx={{
                              color: '#94a3b8',
                              fontSize: '0.8rem',
                            }}
                          >
                            →{' '}
                            {stageNameById.get(
                              transition
                                .to_stage_definition_id,
                            ) ?? 'Etapa no disponible'}
                          </Typography>
                        </Stack>
                      }
                    />
                  </Paper>
                ),
              )}

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2.5,
                  border: `1px solid ${alpha(
                    '#fbbf24',
                    0.2,
                  )}`,
                  bgcolor:
                    'rgba(15,23,42,0.28)',
                  px: 1,
                  py: 0.55,
                }}
              >
                <FormControlLabel
                  value={
                    EXCEPTIONAL_VALUE
                  }
                  control={<Radio />}
                  label={
                    <Stack
                      direction="row"
                      spacing={0.8}
                      alignItems="center"
                    >
                      <AltRouteRounded
                        sx={{
                          color: '#fbbf24',
                        }}
                      />

                      <Typography
                        sx={{
                          color: '#f8fafc',
                          fontWeight: 850,
                        }}
                      >
                        Mover a otra etapa...
                      </Typography>
                    </Stack>
                  }
                />
              </Paper>
            </Stack>
          </RadioGroup>

          {selectedOption
          === EXCEPTIONAL_VALUE ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha(
                  '#fbbf24',
                  0.22,
                )}`,
                bgcolor:
                  'rgba(15,23,42,0.36)',
                px: 1.3,
                py: 1.2,
              }}
            >
              <Stack spacing={1.2}>
                <Stack
                  direction="row"
                  spacing={0.8}
                  alignItems="center"
                >
                  <FlagRounded
                    sx={{
                      color: '#fbbf24',
                    }}
                  />

                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 900,
                    }}
                  >
                    Movimiento excepcional
                  </Typography>
                </Stack>

                <Alert
                  severity="warning"
                  variant="outlined"
                >
                  Utiliza esta opción únicamente
                  cuando el recorrido previsto no
                  sea suficiente. El motivo quedará
                  registrado en el historial.
                </Alert>

                <TextField
                  select
                  size="small"
                  label="Etapa de destino"
                  value={
                    exceptionalTargetStageId
                  }
                  onChange={(event) =>
                    setExceptionalTargetStageId(
                      event.target.value,
                    )
                  }
                  fullWidth
                  required
                >
                  {exceptionalTargetStages.map(
                    (stage) => (
                      <MenuItem
                        key={stage.id}
                        value={stage.id}
                      >
                        {stage.name}
                      </MenuItem>
                    ),
                  )}
                </TextField>

                <TextField
                  size="small"
                  label="Motivo"
                  value={
                    exceptionalReason
                  }
                  onChange={(event) =>
                    setExceptionalReason(
                      event.target.value,
                    )
                  }
                  multiline
                  minRows={3}
                  fullWidth
                  required
                />
              </Stack>
            </Paper>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          disabled={
            completeStageMutation.isPending
          }
        >
          Cancelar
        </Button>

        <Button
          variant="contained"
          disabled={
            completeStageMutation.isPending
          }
          onClick={
            handleSubmit
          }
        >
          Confirmar y continuar
        </Button>
      </DialogActions>
    </Dialog>
  );
}